import { memo, useCallback, useMemo, } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import { useHomeContext } from "@pages/Home/HomeContext";
import { IOption, Sort } from "@models";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import SortIcon from "@components/SortIcon";
import { SelectSingle } from "@components/Select";
import { useMetrics } from "@core/CommonDataContext";
import { HOME_SORT_BY_METRICS_ORDER } from "@models/Metric";
import SelectAmong from "@vms/SelectAmong";
import Tiles from "@pages/Home/components/TilesView/Tiles";
import { DEFAULT_SORT_DIRECTION } from "@components/Table/TableSorting";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { useHomeTilesData } from "@api/useHomeTilesData";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";
import { ProfileExportPptRequest } from "@pages/Profile/components/Header/Header";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";

const TilesView: FCX = () => {
    const metrics = useMetrics();
    const {
        sortContext: {
            sortKey,
            setSortKey,
            sortDirection,
            setSortDirection,
        },
    } = useHomeContext();

    const { amongPayload } = useSelectAmongContext();
    const {
        isLoading,
        data,
    } = useHomeTilesData(amongPayload);

    const sortByOptions = useMemo(
        (): Array<IOption<typeof sortKey> & { defaultDirection?: Sort }> => [
            {
                id: 'name',
                title: 'Name',
                defaultDirection: Sort.Asc,
            },
            {
                id: 'macStage',
                title: 'MAC Stage',
                defaultDirection: Sort.Asc,
            },
            ...HOME_SORT_BY_METRICS_ORDER.map((metricId) => ({
                id: `metric${metricId}` as typeof sortKey,
                title: `% ${metrics.find((metric) => metric.id === metricId)?.name ?? ""}`
            }))
        ],
        [metrics]
    );

    const toggleSortingDirection = useCallback(
        () => setSortDirection(sortDirection === Sort.Asc ? Sort.Desc : Sort.Asc),
        [setSortDirection, sortDirection]
    );

    const toggleSorting = useCallback((sortBy: string) => {
        const newSortOption = sortByOptions.find((option) => option.id === sortBy);
        const defaultDirection = newSortOption?.defaultDirection ?? DEFAULT_SORT_DIRECTION;

        if (sortKey !== defaultDirection) {
            setSortDirection(defaultDirection);
        }

        setSortKey(sortBy);
    }, [sortKey, setSortKey, sortByOptions, setSortDirection]);

    const { dataForSorting } = useFilteredKeywordsData(data, '');

    const exportPayload = useMemo(
        (): ProfileExportPptRequest => ({
            ...amongPayload,
        }),
        [amongPayload]
    );

    return (
        <>
            <HomeHeader
                resultCount={dataForSorting.length}
                isLoading={isLoading}
            >
                <SelectSingle
                    className="HomeHeaderControls__view-as"
                    value={sortKey}
                    setValue={toggleSorting}
                    options={sortByOptions}
                    label={
                        <div className="HomeHeaderControls__view-as-label">
                            <span>SORT BY:</span>
                            <div
                                className="HomeHeaderControls__sort-icon-container"
                                onClick={toggleSortingDirection}
                            >
                                <SortIcon
                                    className="HomeHeaderControls__sort-icon"
                                    direction={sortDirection}
                                />
                            </div>
                        </div>
                    }
                    testId="selectHomeHeaderViewAs"
                />
            </HomeHeader>
            <HomeSubHeader>
                <SelectAmong
                    label="AMONG:"
                    useGroupsLimit
                    useCoExclusions
                />
            </HomeSubHeader>
            <Tiles
                rawData={data}
                isLoading={isLoading}
            />
            <HomeActionsBar exportPayload={exportPayload}/>
        </>
    );
};

export default memo(TilesView);